import React, { useState } from "react";
import {
  Box,
  Popover,
  Typography,
  IconButton,
  Avatar,
  Link,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Check as CheckIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints, IMAGE_URL } from "src/network/endpoints";
import toast from "react-hot-toast";
import GroupRequest from "./components/groupRequest";

/**
 * Styled notification card component
 * Makes the card darker if notification is unread
 */
const NotificationCard = styled(Box)(({ isRead }) => ({
  padding: "12px 16px",
  cursor: "pointer",
  transition: "background-color 0.2s",
  backgroundColor: isRead ? "#ffffff" : "rgba(0, 0, 0, 0.04)",
  "&:hover": {
    backgroundColor: isRead ? "rgba(0, 0, 0, 0.02)" : "rgba(0, 0, 0, 0.06)",
  },
}));

/**
 * Styled popover paper component
 */
const StyledPopover = styled(Popover)(({ theme }) => ({
  "& .MuiPopover-paper": {
    width: 400,
    maxHeight: 600,
    borderRadius: 2,
    boxShadow: theme.shadows[8],
    [theme.breakpoints.down("sm")]: {
      width: "90vw",
      maxWidth: 400,
    },
  },
}));

/**
 * NotificationDropdown component
 * Main dropdown component for displaying notifications
 * Features:
 * - Fetches notifications from API
 * - Displays different notification types
 * - Mark all as read functionality
 * - Clear all notifications functionality
 * - Navigate to notification details
 * - Shows accept/reject buttons for group requests and employee requests
 * 
 * @param {Object} anchorEl - Anchor element for popover positioning
 * @param {Function} onClose - Callback function when dropdown closes
 */
const NotificationDropdown = ({ anchorEl, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = !!user;
  const [page, setPage] = useState(1);
  const limit = 20;

  // Fetch notifications from API (only when user is logged in and dropdown is open)
  const {
    data: notificationsData,
    isLoading,
    refetch,
  } = useQuery(
    ["notifications", page, limit],
    async ({ signal }) => {
      const res = await axiosInstance.get(ApiEndPoints.NOTIFICATION.list, {
        signal,
        params: {
          page,
          limit,
        },
      });
      return res.data.data;
    },
    {
      enabled: isAuthenticated && Boolean(anchorEl),
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const notifications = notificationsData?.notifications || [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  // Mark all notifications as read
  const markAllAsReadMutation = useMutation(
    async () => {
      const res = await axiosInstance.put(
        ApiEndPoints.NOTIFICATION.markAllAsRead
      );
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success("all notifications marked as read");
        queryClient.invalidateQueries(["notifications"]);
        queryClient.invalidateQueries(["notifications-count"]);
      },
      onError: (error) => {
        console.error("Error marking all as read:", error);
        toast.error(
          error?.response?.data?.message || "failed to mark all as read"
        );
      },
    }
  );

  // Mark single notification as read
  const markAsReadMutation = useMutation(
    async (notificationId) => {
      const res = await axiosInstance.put(
        ApiEndPoints.NOTIFICATION.markAsRead(notificationId)
      );
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["notifications"]);
        queryClient.invalidateQueries(["notifications-count"]);
      },
      onError: (error) => {
        console.error("Error marking notification as read:", error);
        // Don't show error toast for individual notification read failures
      },
    }
  );

  // Clear all notifications
  const clearAllMutation = useMutation(
    async () => {
      const res = await axiosInstance.delete(ApiEndPoints.NOTIFICATION.clearAll);
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success("all notifications cleared");
        queryClient.invalidateQueries(["notifications"]);
        queryClient.invalidateQueries(["notifications-count"]);
      },
      onError: (error) => {
        console.error("Error clearing all notifications:", error);
        toast.error(
          error?.response?.data?.message || "failed to clear notifications"
        );
      },
    }
  );

  // Handle mark all as read
  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  // Handle clear all
  const handleClearAll = () => {
    if (window.confirm("are you sure you want to clear all notifications?")) {
      clearAllMutation.mutate();
    }
  };

  // Handle notification click - mark as read and navigate to the notification target
  const handleNotificationClick = (notification) => {
    // Mark notification as read if it's unread
    if (!notification.isRead && notification._id) {
      markAsReadMutation.mutate(notification._id);
    }

    // Handle post_created notifications - redirect to post detail page
    if (notification.type === "post" && notification.subType === "post_created") {
      const postId = notification.meta?.postId || notification.navigateTo?.split("/post/")[1];
      if (postId) {
        navigate(`/post/${postId}`);
        onClose();
        return;
      }
    }

    if (notification.navigateTo) {
      // For employee_request, navigate using base URL + navigateTo path
      if (notification.subType === "employee_request") {
        window.location.href = `${window.location.origin}${notification.navigateTo}`;
      } else {
        navigate(notification.navigateTo);
      }
      onClose();
    }
  };

  // Get notification icon based on type/subType
  const getNotificationIcon = (notification) => {
    if (notification.subType === "group_request") {
      // For group requests, show sender's profile picture
      if (notification.image) {
        return (
          <Avatar
            src={`${IMAGE_URL}${notification.image}`}
            sx={{ width: 40, height: 40 }}
          />
        );
      }
      return (
        <Avatar sx={{ width: 40, height: 40, bgcolor: "#9C27B0" }}>
          <ShareIcon />
        </Avatar>
      );
    }

    if (notification.subType === "employee_request") {
      // For employee requests, show sender's profile picture if available
      if (notification.senderId?.image?.profilePic) {
        return (
          <Avatar
            src={`${IMAGE_URL}${notification.senderId.image.profilePic}`}
            sx={{ width: 40, height: 40 }}
          />
        );
      }
      return (
        <Avatar sx={{ width: 40, height: 40, bgcolor: "#9C27B0" }}>
          <ShareIcon />
        </Avatar>
      );
    }

    // Default icon for other notification types
    return (
      <Avatar sx={{ width: 40, height: 40, bgcolor: "#9C27B0" }}>
        <ShareIcon />
      </Avatar>
    );
  };

  // Render notification item based on type
  const renderNotificationItem = (notification) => {
    // Import and render specific component based on subType
    if (notification.subType === "group_request") {
      return (
        <NotificationCard
          key={notification._id}
          isRead={notification.isRead}
          onClick={() => handleNotificationClick(notification)}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            {getNotificationIcon(notification)}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#9C27B0",
                  mb: 0.5,
                  fontSize: "0.875rem",
                }}
              >
                {notification.title || "group invitation"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.8125rem",
                  mb: 1,
                }}
              >
                {notification.description}
              </Typography>
              <GroupRequest
                notification={notification}
                onActionComplete={refetch}
              />
            </Box>
          </Box>
        </NotificationCard>
      );
    }

    if (notification.subType === "employee_request") {
      return (
        <NotificationCard
          key={notification._id}
          isRead={notification.isRead}
          onClick={() => handleNotificationClick(notification)}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            {getNotificationIcon(notification)}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#9C27B0",
                  mb: 0.5,
                  fontSize: "0.875rem",
                }}
              >
                {notification.title || "employee request"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.8125rem",
                }}
              >
                {notification.description}
              </Typography>
            </Box>
          </Box>
        </NotificationCard>
      );
    }

    // Default notification item for other types
    return (
      <NotificationCard
        key={notification._id}
        isRead={notification.isRead}
        onClick={() => handleNotificationClick(notification)}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          {getNotificationIcon(notification)}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#9C27B0",
                mb: 0.5,
                fontSize: "0.875rem",
              }}
            >
              {notification.title || "notification"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "0.8125rem",
              }}
            >
              {notification.description}
            </Typography>
          </Box>
        </Box>
      </NotificationCard>
    );
  };

  return (
    <StyledPopover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "lowercase",
          }}
        >
          notifications
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            size="small"
            onClick={handleMarkAllAsRead}
            disabled={markAllAsReadMutation.isLoading || unreadCount === 0}
            sx={{ color: "#2196F3" }}
          >
            {markAllAsReadMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <CheckIcon fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={handleClearAll}
            disabled={clearAllMutation.isLoading || notifications.length === 0}
            sx={{ color: "#2196F3" }}
          >
            {clearAllMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <DeleteIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* Notifications List */}
      <Box
        sx={{
          maxHeight: 400,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#ccc",
            borderRadius: "3px",
          },
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 4,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 4,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              no notifications
            </Typography>
          </Box>
        ) : (
          notifications.map((notification) => renderNotificationItem(notification))
        )}
      </Box>

      {/* Footer - See All Link */}
      {notifications.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 1.5,
            }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                navigate("/notifications");
                onClose();
              }}
              sx={{
                color: "#2196F3",
                textDecoration: "none",
                textTransform: "lowercase",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              see all
            </Link>
          </Box>
        </>
      )}
    </StyledPopover>
  );
};

export default NotificationDropdown;

